import { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useLogger } from '@packages/shared/core/common/hooks';
import { useQuizStep } from '@packages/shared/core/quiz/hooks';
import { AnswerKey, PreferenceIntroStepData, QuizScreenProps } from '@packages/shared/core/quiz/entities';
import { getZodiac } from '@packages/shared/core/user/store/selectors';
import { UserState } from '@packages/shared/core/user/store/state';

type PreferenceIntroStepProps = {
  id: string;
  renderQuizScreen: (stepData: QuizScreenProps) => ReactNode;
};

const areaOfImprovementMap: Record<string, string> = {
  'love happiness': 'Love happiness',
  'personal fulfillment': 'Personal growth',
  'guidance and support': 'Personal growth',
  'self-confidence': 'Self confidence',
  'all the above': 'Personal growth',
  'not sure': 'Personal growth',
};

const goalToAchieveMap: Partial<Record<AnswerKey, Record<string, string>>> = {
  'find out about my ex': {
    'find out if we’ll get back together': 'Get back with ex',
    'find out if ex still has feelings': 'Feelings discovery',
    'find out if my ex has someone else': "Reveal ex's life",
    'move on after breakup': 'Move on',
    'other (please specify)': 'Find out about ex',
  },
  'give advice for my love life': {
    'improve my relationship': 'Improve love life',
    'find my soulmate': 'Find soulmate',
    'check compatibility': 'Compatibility test',
    'heal from heartbreak': 'Heart healing',
    'other (please specify)': 'Improve love life',
  },
  'give guidance on my future': {
    'get advice on decision': 'Receive guidance',
    'find clarity in life': 'Gain clarity',
    'receive insights about future': 'Discover future',
    'get understanding and support': 'Receive guidance',
    'other (please specify)': 'Receive guidance',
  },
  'all above': {
    'find out if we can get back together': 'Get back with ex',
    'get clarity in love & life': 'Get clarity',
    'explore future relationship paths': 'Improve love life',
    'discover more about myself': 'Discover future',
    'other (please specify)': 'Receive guidance',
  },
};

const PreferenceIntroStep = ({ id, renderQuizScreen }: PreferenceIntroStepProps) => {
  const logger = useLogger();

  const { back, next, stepData, userPreferences } = useQuizStep(id);

  const missingInLifeAnswer = (userPreferences as UserState & { missingInLife: string }).missingInLife;

  const userGoalAnswer = (userPreferences as UserState & { yourGoal: string }).yourGoal;

  const goal = userPreferences.goal;

  const zodiac = useSelector(getZodiac);
  const areaOfImprovement = areaOfImprovementMap[missingInLifeAnswer];
  const goalToAchieve = (goalToAchieveMap[goal as AnswerKey] ?? {})[userGoalAnswer];

  if (!stepData) {
    logger.error(new TypeError(`Data for ${id} step not found`));
    return null;
  }

  const goBack = async () => back();

  const onContinue = async () => {
    await next();
  };

  return (
    <>
      {renderQuizScreen({
        onContinue,
        onBack: goBack,
        areaOfImprovement,
        goalToAchieve,
        userPreferences,
        zodiac,
        ...(stepData as PreferenceIntroStepData),
      })}
    </>
  );
};

export default PreferenceIntroStep;
